import React, { useState } from "react";
import { Button, ButtonGroup, Col, Form, Modal, Row } from "react-bootstrap";
import {
    Controller,
    SubmitErrorHandler,
    SubmitHandler,
    useForm,
} from "react-hook-form";
import {
    useLoaderData,
    useLocation,
    useNavigate,
    useOutletContext,
} from "react-router";
import Select from "react-select";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { alertActions } from "../../features/alert/alertSlice";
import { selectSetting } from "../../features/global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import {
    createPlayer,
    deletePlayer,
    getPlayer,
    getPositions,
    getRoster,
    getSiteSetting,
    getTeam,
    updatePlayer,
} from "../../lib/api";
import { canEditTeam, displayFormAlert } from "../../lib/utils";
import {
    IAPIResponse,
    IPlayer,
    IPosition,
    ISeason,
    ISiteSetting,
    ITeam,
} from "../../types/api";
import { IPlayerFormData } from "../../types/forms";
import { TRosterContext } from "../../types/shared";

export const loader = async ({ params }: { params: any }) => {
    const { teamId, playerId } = params;

    const getPlayerPromise = playerId ? getPlayer(playerId) : Promise.resolve();

    const [
        { team: currentTeam },
        player,
        positions,
        maxRosterSize,
        currentLeagueYear,
    ]: [
        team: IAPIResponse & { team: ITeam },
        player: (IAPIResponse & { player: IPlayer }) | void,
        IAPIResponse & { positions: IPosition[] },
        maxRosterSize: IAPIResponse & {
            setting: ISiteSetting | ISiteSetting[];
        },
        currentLeagueYear: IAPIResponse & {
            setting: ISiteSetting | ISiteSetting[];
        },
    ] = await Promise.all([
        getTeam(teamId),
        getPlayerPromise,
        getPositions(),
        getSiteSetting("wibb_max_roster_size"),
        getSiteSetting("wibb_current_league_year"),
    ]);

    return {
        teamId,
        player: player?.player || undefined,
        positions: positions.positions as IPosition[],
        currentTeam,
        maxRosterSize,
        currentLeagueYear,
    };
};

const Player = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const isAddingPlayer = /team\/\d+\/roster\/add\/?$/.test(location.pathname);
    const [show, setShow] = useState<boolean>(false);
    const navigate = useNavigate();
    const user = useAppSelector(state => state.auth.user);
    const [roster, setRoster] = useOutletContext<TRosterContext>();

    const {
        player,
        positions,
        currentTeam,
        maxRosterSize: {
            setting: { setting: maxRosterSize },
        },
        currentLeagueYear: {
            setting: { setting: currentLeagueYear },
        },
    } = useLoaderData() as {
        player: IPlayer | undefined;
        positions: { id: number; position: string }[];
        currentTeam: ITeam;
        roster: IAPIResponse & {
            players: IPlayer[];
        };
        maxRosterSize: IAPIResponse & {
            setting: ISiteSetting;
        };
        currentLeagueYear: IAPIResponse & {
            setting: ISiteSetting;
        };
    };

    const rosterCount = roster.filter(r => r.active === 1).length;

    const playerFormSchema = yup.object().shape({
        isPrimary: yup.boolean().required(),
        active: yup.number().required("Please set the player status"),
        firstName: yup.string().required("Player First Name is required"),
        lastName: yup.string().required("Player Last Name is required"),
        uniformNumber: yup
            .number()
            .optional()
            .nullable()
            .transform((curr, orig) => (orig === "" ? undefined : curr))
            .typeError("Invalid number"),
        dob: yup
            .string()
            .required("Player Date of Birth is required")
            .typeError("Invalid date of birth provided")
            .test("is-valid-dob", "Invalid date of birth provided", dob => {
                if (!dob) return true;
                return !isNaN(new Date(dob).getTime());
            }),
        email: yup
            .string()
            .required("Parent email is required")
            .email("Parent email is invalid"),
        positions: yup.array(
            yup.object().shape({
                value: yup.number().required(),
                label: yup.string().required(),
            }),
        ),
    });

    const {
        register,
        handleSubmit,
        control,
        setValue,
        getValues,
        watch,
        reset,
        formState: { errors },
    } = useForm<IPlayerFormData>({
        mode: "onSubmit",
        resolver: yupResolver(playerFormSchema),
    });

    const onSubmit: SubmitHandler<IPlayerFormData> = async data => {
        const playerPromise = isAddingPlayer
            ? createPlayer(data, currentTeam.id)
            : updatePlayer(data, currentTeam.id, player!.id);

        await playerPromise
            .then(response => {
                const alertAction = isAddingPlayer ? "created" : "updated";

                dispatch(
                    alertActions.success({
                        title: `Player ${alertAction} successfully`,
                        text: `${data.firstName} ${data.lastName}\n${data.email}`,
                        dismissible: true,
                    }),
                );

                reset();
            })
            .catch(e => {
                const alertAction = isAddingPlayer ? "adding" : "updating";

                dispatch(
                    alertActions.error({
                        title: `The was a problem ${alertAction} the player.`,
                        text: e,
                        dismissible: true,
                    }),
                );

                reset();
            })
            .finally(async () => {
                const roster = await getRoster(currentTeam.id);
                setRoster(roster.players);
            });
    };

    const handleDelete: React.MouseEventHandler<
        HTMLButtonElement
    > = async () => {
        setShow(false);
        await deletePlayer(player!.id, currentTeam!.id)
            .then(() => {
                dispatch(
                    alertActions.success({
                        title: "Player deleted successfully",
                        dismissible: true,
                        persist: true,
                    }),
                );
                navigate(`/coachs-clubhouse/team/${currentTeam.id}/roster`);
            })
            .catch(e => {
                dispatch(
                    alertActions.error({
                        title: "The was a problem deleting the player.",
                        text: e,
                        dismissible: true,
                    }),
                );
            })
            .finally(async () => {
                const roster = await getRoster(currentTeam.id);
                setRoster(roster.players);
            });
    };

    const playerPositions = player?.positions
        ? player.positions.map(p => {
              return {
                  value: parseInt(p.toString()),
                  label: positions.find(i => i.id == p)?.position as string,
              };
          })
        : [];

    const isPlayerActive = isAddingPlayer
        ? watch("active", 1)
        : watch("active", player?.active);

    const teamAtMaxSize = isAddingPlayer && maxRosterSize < rosterCount;

    const onError: SubmitErrorHandler<IPlayerFormData> = data => {
        console.log(data);
    };
    return (
        <section className="playerInfo">
            <Form
                onSubmit={handleSubmit(onSubmit, onError)}
                id="playerInfoForm"
            >
                <Form.Control
                    type="hidden"
                    {...register("isPrimary", { value: true })}
                />
                <Row>
                    <Col>
                        <h2>
                            {isAddingPlayer
                                ? "Adding Player"
                                : `Editing ${player?.firstName} ${player?.lastName}`}
                        </h2>
                        {maxRosterSize < rosterCount && (
                            <h3 className="text-danger">
                                This team is at the maximum active player count
                                of {maxRosterSize}. Please deactivate players to
                                bring your active player count down before
                                adding/activating new players.
                            </h3>
                        )}
                    </Col>
                </Row>
                <Row>
                    <Form.Group
                        controlId="active"
                        as={Col}
                        xs={6}
                        lg={2}
                        className="col"
                    >
                        <Form.Label>Status</Form.Label>
                        <Form.Control
                            type="hidden"
                            {...register("active", {
                                value: isAddingPlayer
                                    ? 1
                                    : getValues("active") || player?.active,
                            })}
                        />
                        <ButtonGroup>
                            <Button
                                variant={
                                    isPlayerActive
                                        ? "primary"
                                        : "outline-primary"
                                }
                                onClick={() => setValue("active", 1)}
                                disabled={
                                    isPlayerActive == 0 &&
                                    maxRosterSize < rosterCount
                                }
                            >
                                Active
                            </Button>
                            <Button
                                variant={
                                    isPlayerActive
                                        ? "outline-primary"
                                        : "primary"
                                }
                                onClick={() => setValue("active", 0)}
                            >
                                Inactive
                            </Button>
                        </ButtonGroup>
                    </Form.Group>
                    <Form.Group
                        controlId="firstName"
                        as={Col}
                        xs={6}
                        lg={5}
                        className="col"
                    >
                        <Form.Label>First Name</Form.Label>
                        <Form.Control
                            type="text"
                            {...register("firstName", {
                                value: player?.firstName,
                            })}
                            aria-invalid={errors.firstName ? "true" : "false"}
                            disabled={teamAtMaxSize}
                        />
                        {errors.firstName &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.firstName.message as string,
                            })}
                    </Form.Group>
                    <Form.Group
                        controlId="lastName"
                        as={Col}
                        xs={6}
                        lg={5}
                        className="col"
                    >
                        <Form.Label>Last Name</Form.Label>
                        <Form.Control
                            type="text"
                            {...register("lastName", {
                                value: player?.lastName,
                            })}
                            aria-invalid={errors.lastName ? "true" : "false"}
                            disabled={teamAtMaxSize}
                        />
                        {errors.lastName &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.lastName.message as string,
                            })}
                    </Form.Group>
                </Row>
                <Row>
                    <Form.Group
                        controlId="uniformNumber"
                        as={Col}
                        xs={6}
                        lg={4}
                        className="col"
                    >
                        <Form.Label>Uniform #</Form.Label>
                        <Form.Control
                            type="text"
                            {...register("uniformNumber", {
                                value: player?.uniformNumber,
                            })}
                            aria-invalid={
                                errors.uniformNumber ? "true" : "false"
                            }
                            disabled={teamAtMaxSize}
                        />
                        {errors.uniformNumber &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.uniformNumber.message as string,
                            })}
                    </Form.Group>
                    <Form.Group
                        controlId="dob"
                        as={Col}
                        xs={6}
                        lg={4}
                        className="col"
                    >
                        <Form.Label>Date of Birth</Form.Label>
                        <Form.Control
                            type="text"
                            {...register("dob", {
                                value: player?.dob
                                    ? new Date(
                                          `${player?.dob}T00:00:00` as string,
                                      ).toLocaleDateString("en-US", {
                                          year: "numeric",
                                          month: "2-digit",
                                          day: "2-digit",
                                      })
                                    : undefined,
                            })}
                            aria-invalid={errors.dob ? "true" : "false"}
                            disabled={teamAtMaxSize}
                        />
                        {errors.dob &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.dob.message as string,
                            })}
                    </Form.Group>
                    <Form.Group
                        controlId="positions"
                        as={Col}
                        xs={6}
                        lg={4}
                        className="col"
                    >
                        <Form.Label>Position(s)</Form.Label>
                        <Controller
                            name="positions"
                            control={control}
                            defaultValue={playerPositions}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    isMulti
                                    isSearchable={false}
                                    options={positions.map(p => {
                                        return {
                                            value: parseInt(p.id.toString()),
                                            label: p.position,
                                        };
                                    })}
                                    defaultValue={playerPositions}
                                    isDisabled={teamAtMaxSize}
                                />
                            )}
                        />
                    </Form.Group>
                </Row>
                <Row>
                    <Col xs={6}>
                        <Form.Group controlId="email">
                            <Form.Label>Parent Email</Form.Label>
                            <Form.Control
                                type="email"
                                {...register("email", {
                                    value: player?.email,
                                })}
                                aria-invalid={errors.email ? "true" : "false"}
                                disabled={teamAtMaxSize}
                            />
                            {errors.email &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.email.message as string,
                                })}
                        </Form.Group>
                    </Col>
                </Row>
                {canEditTeam(currentTeam) && (
                    <Row>
                        <Col>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={teamAtMaxSize}
                            >
                                {isAddingPlayer
                                    ? "Add Player"
                                    : "Update Information"}
                            </Button>
                            <Button
                                variant="danger"
                                type="button"
                                onClick={() => setShow(true)}
                            >
                                Delete Player
                            </Button>
                        </Col>
                    </Row>
                )}
            </Form>
            <Modal show={show} onHide={() => setShow(false)}>
                <Modal.Header>
                    <Modal.Title>Delete player?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <b>Are you sure you want to delete this player?</b>
                    <br />
                    {player?.firstName} {player?.lastName}
                    <br />
                    {player?.dob}
                    <br />
                    {player?.email}
                    <br />
                    <br />
                    <b>Note:</b> Deleting this player will remove them from your
                    team and could affect their playoff eligibility. If you'd
                    like to keep them on your team and maintain their
                    eligibility (if applicable), instead set them to an
                    "Inactive" status.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShow(false)}>
                        No
                    </Button>
                    <Button variant="danger" onClick={handleDelete}>
                        Yes
                    </Button>
                </Modal.Footer>
            </Modal>
        </section>
    );
};

export default Player;
